import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Trash2, Plus, Mail, Lock, Shield } from 'lucide-react';
import api from '../lib/api';

// Source → visual hint badge. Custom ones are the only deletable rows.
const SOURCE_BADGE = {
  env: { label: 'Secret', icon: Lock, cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40', tip: 'Configurado en SUPERADMIN_ALERT_EMAILS (sólo se modifica desde Emergent Secrets + Deploy)' },
  custom: { label: 'Custom', icon: Mail, cls: 'bg-sky-500/20 text-sky-300 border-sky-500/40', tip: 'Agregado desde este panel' },
  db_admin: { label: 'Admin', icon: Shield, cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', tip: 'Usuario Master/Super Admin activo en la plataforma' },
};

export function AlertRecipientsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState({});
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/diagnostics/alert-recipients');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    setAdding(true);
    try {
      await api.post('/diagnostics/alert-recipients', { email, label: newLabel.trim() || null });
      toast.success(`${email} añadido a las alertas`);
      setNewEmail('');
      setNewLabel('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo añadir');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    setRemoving((prev) => ({ ...prev, [email]: true }));
    try {
      await api.delete(`/diagnostics/alert-recipients/${encodeURIComponent(email)}`);
      toast.success(`${email} removido`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo remover');
    } finally {
      setRemoving((prev) => ({ ...prev, [email]: false }));
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" /> Cargando recipients…
      </div>
    );
  }

  const items = data?.recipients || [];

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="alert-recipients-card">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="font-semibold flex items-center gap-2"><Mail size={16} /> Notificaciones del sistema</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Correos que reciben TODAS las alertas de super-admin (Ferrovalle login, SOIA, stale-sync, transiciones de puerto, reporte mensual).
        </p>
      </div>

      <form onSubmit={handleAdd} className="px-4 py-3 border-b border-border flex flex-wrap items-end gap-2" data-testid="alert-recipients-form">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Email</label>
          <Input
            type="email"
            placeholder="correo@empresa.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            data-testid="alert-recipients-email"
          />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="text-xs text-muted-foreground">Etiqueta (opcional)</label>
          <Input
            type="text"
            placeholder="Operaciones, CEO…"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            data-testid="alert-recipients-label"
          />
        </div>
        <Button type="submit" disabled={adding} data-testid="alert-recipients-add">
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          <span className="ml-1.5">Añadir</span>
        </Button>
      </form>

      <ul className="divide-y divide-border" data-testid="alert-recipients-list">
        {items.length === 0 ? (
          <li className="px-4 py-8 text-center text-muted-foreground text-sm">Sin recipients configurados</li>
        ) : items.map((r) => {
          const badge = SOURCE_BADGE[r.source] || SOURCE_BADGE.custom;
          const Icon = badge.icon;
          const isRemoving = removing[r.email];
          return (
            <li
              key={`${r.source}-${r.email}`}
              className="px-4 py-3 flex items-center gap-3 flex-wrap"
              data-testid={`recipient-${r.email}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm">{r.email}</span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${badge.cls}`}
                    title={badge.tip}
                  >
                    <Icon size={10} />
                    {badge.label}
                  </span>
                </div>
                {r.label && <p className="text-xs text-muted-foreground mt-0.5">{r.label}</p>}
                {r.added_by && (
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                    Añadido por {r.added_by}
                  </p>
                )}
              </div>
              {r.editable ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(r.email)}
                  disabled={isRemoving}
                  className="text-destructive hover:text-destructive shrink-0"
                  data-testid={`recipient-remove-${r.email}`}
                  title="Remover"
                >
                  {isRemoving ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground/60 shrink-0">
                  {r.source === 'env' ? 'desde Secrets' : 'desde Usuarios'}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
